import { useMemo } from "react";
import {
  type GridColumnProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { stripAriaSelected } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";
import { useCertificationLogs } from "@/queries";
import { CertificationLog } from "@/models/file-submission";

type EnrichedCertificationLog = CertificationLog & {
  _derivedRevisionId: string;
};

const ACTIVITY_TYPE_STYLES: Record<
  string,
  { bg: string; color: string; border: string }
> = {
  Certify: { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
  Decertify: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
  "Revision Locked": { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  "Revision Unlocked": { bg: "#fffbeb", color: "#92400e", border: "#fde68a" },
};

const VERIFIED_REPORT_STYLE = {
  bg: "#ede9fe",
  color: "#5b21b6",
  border: "#ddd6fe",
};
const DEFAULT_STYLE = { bg: "#f3f4f6", color: "#374151", border: "#e5e7eb" };

function deriveActivityType(
  log: CertificationLog,
  formatReportId: (id: number) => string
): string {
  if (log.StatusUpdate === "Certified") return "Certify";
  if (log.StatusUpdate === "Decertified") return "Decertify";
  if (log.ReviewedReportId != null)
    return `Verified Report ${formatReportId(log.ReviewedReportId)}`;
  if (log.LockAnomalyRunId != null) return "Revision Locked";
  if (log.UnLockAnomalyRunId != null) return "Revision Unlocked";
  if (log.StatusUpdate) return log.StatusUpdate;
  return "-";
}

function deriveRevisionId(
  log: CertificationLog,
  allLogs?: CertificationLog[]
): string {
  if (log.LockAnomalyRunId != null) return String(log.LockAnomalyRunId);
  if (log.UnLockAnomalyRunId != null) return String(log.UnLockAnomalyRunId);

  if (log.ReviewedReportId != null && allLogs) {
    const logTime = log.ReviewerDate
      ? new Date(log.ReviewerDate).getTime()
      : NaN;
    if (Number.isNaN(logTime)) return "-";

    const priorLock = allLogs
      .filter((l) => {
        if (l.LockAnomalyRunId == null || !l.ReviewerDate) return false;
        const lockTime = new Date(l.ReviewerDate).getTime();
        return !Number.isNaN(lockTime) && lockTime <= logTime;
      })
      .sort(
        (a, b) =>
          new Date(b.ReviewerDate ?? "").getTime() -
          new Date(a.ReviewerDate ?? "").getTime()
      )[0];
    if (priorLock) return String(priorLock.LockAnomalyRunId);
  }

  return "-";
}

function deriveStepFromLog(log: CertificationLog): string {
  if (log.StatusUpdate === "Certified") return "Certified";
  if (log.StatusUpdate === "Decertified") return "Decertified";
  if (log.LockAnomalyRunId != null) return "Verify Reports";
  if (log.UnLockAnomalyRunId != null) return "Verify Reports";
  if (log.ReviewedReportId != null) return "Verify Reports";
  if (log.StatusUpdate) return log.StatusUpdate;
  return "-";
}

function formatDateTime(dateStr: string | null | undefined): {
  date: string;
  time: string;
} {
  if (!dateStr) return { date: "-", time: "" };
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(dateStr);
  const d = new Date(hasTimezone ? dateStr : dateStr + "Z");
  if (isNaN(d.getTime())) return { date: "-", time: "" };

  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const time = `${hours}:${minutes} ${ampm}`;
  return { date, time };
}

function ActivityDateCell(props: GridCustomCellProps) {
  const { date, time } = formatDateTime(props.dataItem.ReviewerDate);
  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <div className="d-flex flex-column" style={{ lineHeight: 1.3 }}>
        <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
          {date}
        </span>
        {time && (
          <span className="text-muted" style={{ fontSize: "0.8rem" }}>
            {time}
          </span>
        )}
      </div>
    </td>
  );
}

function RevisionIdCell(props: GridCustomCellProps) {
  const log = props.dataItem as EnrichedCertificationLog;
  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <span style={{ fontSize: "0.95rem" }}>{log._derivedRevisionId}</span>
    </td>
  );
}

function makeActivityTypeCell(formatReportId: (id: number) => string) {
  return function ActivityTypeCell(props: GridCustomCellProps) {
    const activityType = deriveActivityType(
      props.dataItem as CertificationLog,
      formatReportId
    );

    const isVerifiedReport = activityType.startsWith("Verified Report");

    const s =
      ACTIVITY_TYPE_STYLES[activityType] ??
      (isVerifiedReport ? VERIFIED_REPORT_STYLE : DEFAULT_STYLE);

    return (
      <td {...stripAriaSelected(props.tdProps)}>
        <span
          className="lifecycle-tab__badge"
          style={{
            backgroundColor: s.bg,
            color: s.color,
            border: `1px solid ${s.border}`,
          }}
        >
          {activityType}
        </span>
      </td>
    );
  };
}

function UserCell(props: GridCustomCellProps) {
  const reviewer = props.dataItem.Reviewer ?? "-";
  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <span style={{ fontSize: "0.95rem" }}>{reviewer}</span>
    </td>
  );
}

function StatusUpdateCell(props: GridCustomCellProps) {
  const step = deriveStepFromLog(props.dataItem as CertificationLog);
  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <span style={{ fontSize: "0.95rem" }}>{step}</span>
    </td>
  );
}

type LifecycleTabProps = {
  esiidPath: string;
  certificationId: string;
  reportIdToIndex?: Map<number, number>;
};

export default function LifecycleTab({
  esiidPath,
  certificationId,
  reportIdToIndex = new Map(),
}: LifecycleTabProps) {
  const {
    data: logs,
    isPending,
    error,
  } = useCertificationLogs(esiidPath, certificationId);

  const formatReportId = useMemo(
    () =>
      (id: number): string => {
        const idx = reportIdToIndex.get(id);
        return idx != null ? `1.${idx}` : `1.${id}`;
      },
    [reportIdToIndex]
  );

  const columns: GridColumnProps[] = useMemo(
    () => [
      {
        field: "ReviewerDate",
        title: "Activity Date",
        cells: { data: ActivityDateCell },
      },
      {
        field: "LockAnomalyRunId",
        title: "Revision ID",
        cells: { data: RevisionIdCell },
      },
      {
        field: "ActivityType",
        title: "Activity Type",
        cells: { data: makeActivityTypeCell(formatReportId) },
      },
      {
        field: "Reviewer",
        title: "User",
        cells: { data: UserCell },
      },
      {
        field: "StatusUpdate",
        title: "Certification Step",
        cells: { data: StatusUpdateCell },
      },
    ],
    [formatReportId]
  );

  const sorted = useMemo<EnrichedCertificationLog[]>(() => {
    if (!logs || !Array.isArray(logs)) return [];

    const sortedLogs = [...logs].sort((a, b) => {
      const dateA = new Date(a.ReviewerDate ?? "").getTime();
      const dateB = new Date(b.ReviewerDate ?? "").getTime();
      if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
      if (Number.isNaN(dateA)) return 1;
      if (Number.isNaN(dateB)) return -1;
      return dateB - dateA;
    });

    return sortedLogs.map((log) => ({
      ...log,
      _derivedRevisionId: deriveRevisionId(log, logs),
    }));
  }, [logs]);

  if (error) {
    return (
      <p className="text-danger mt-3">
        Error loading lifecycle data: {error.message}
      </p>
    );
  }

  return (
    <div className="pt-4">
      <div className="lifecycle-grid lifecycle-tab__container rounded-3 overflow-hidden">
        <div className="lifecycle-tab__header">Certification Lifecycle</div>

        {isPending ? (
          <div
            className="lifecycle-tab__empty"
            aria-busy="true"
            aria-live="polite"
          >
            <span className="text-muted">Loading lifecycle data...</span>
          </div>
        ) : sorted.length === 0 ? (
          <div className="lifecycle-tab__empty" aria-live="polite">
            <span className="text-muted">
              No lifecycle activity recorded yet.
            </span>
          </div>
        ) : (
          <KendoTableClientState
            data={sorted}
            columns={columns}
            filterable={false}
          />
        )}
      </div>
    </div>
  );
}
